import './UpdateBanner.css';
import { reloadPage } from './reloadPage';
import { useVersionCheck } from './useVersionCheck';
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';

interface UpdateBannerProps {
    /** Commit this bundle was built from, i.e. `import.meta.env.VITE_GIT_COMMIT_HASH`. Empty keeps the check off. */
    currentVersion: string | undefined;
    /** The router's current pathname. A change while an update waits triggers the reload. */
    pathname: string;
    message?: ReactNode;
    refreshLabel?: ReactNode;
    /** Overrides the default placement (fixed, centred, 14px from the viewport top). */
    style?: CSSProperties;
}

/**
 * Tells the user a newer bundle is live and offers a reload.
 *
 * A tab left open keeps running the bundle it loaded, so after a deploy it talks to
 * a backend it was not built against. Two escapes: this banner's Refresh button, and
 * a hard reload on the next route change — navigating is the cheapest moment to
 * swap bundles, because the page the user was reading is on its way out anyway.
 *
 * Takes the pathname instead of reading the router, so it does not depend on the
 * react-router major each app is on.
 */
export const UpdateBanner = ({
    currentVersion,
    pathname,
    message = 'A new version has been released',
    refreshLabel = 'Refresh',
    style,
}: UpdateBannerProps) => {
    const isUpdateAvailable = useVersionCheck(currentVersion);
    const [isRefreshing, setRefreshing] = useState(false);
    const previousPathname = useRef(pathname);

    useEffect(() => {
        const changedPage = previousPathname.current !== pathname;
        previousPathname.current = pathname;

        // Only a pathname change: a filter written into the query string must not
        // throw the page away under the user. The router has already pushed the new
        // URL, so the reload lands on the page the user navigated to.
        if (changedPage && isUpdateAvailable) {
            reloadPage();
        }
    }, [isUpdateAvailable, pathname]);

    if (!isUpdateAvailable) return null;

    return (
        <div role="status" aria-live="polite" className="cf-update-banner" style={style}>
            <div className="cf-update-banner__pill">
                <span className="cf-update-banner__message">
                    <span className="cf-update-banner__dot" />
                    {message}
                </span>
                <button
                    type="button"
                    onClick={() => {
                        setRefreshing(true);
                        reloadPage();
                    }}
                    className="cf-update-banner__refresh"
                >
                    <RefreshIcon spinning={isRefreshing} />
                    {refreshLabel}
                </button>
            </div>
        </div>
    );
};

/** lucide's `RefreshCw`, inlined so this folder needs no icon package. */
const RefreshIcon = ({ spinning }: { spinning: boolean }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className={spinning ? 'cf-update-banner__icon cf-update-banner__icon--spinning' : 'cf-update-banner__icon'}
    >
        <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
        <path d="M21 3v5h-5" />
        <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
        <path d="M8 16H3v5" />
    </svg>
);
